import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Avatar,
  TablePagination,
  LinearProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search,
  FilterList,
  RemoveRedEye,
  AccessTime,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  QuestionAnswer,
  Autorenew
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';

const statusChipMap = {
  'Applied': { color: 'primary', icon: <AccessTime fontSize="small" /> },
  'Screening': { color: 'info', icon: <Autorenew fontSize="small" /> },
  'Interview': { color: 'secondary', icon: <QuestionAnswer fontSize="small" /> },
  'Offer': { color: 'success', icon: <CheckCircle fontSize="small" /> },
  'Rejected': { color: 'error', icon: <Cancel fontSize="small" /> },
  'On Hold': { color: 'warning', icon: <HourglassEmpty fontSize="small" /> }
};

const priorityMap = {
  'P0': { text: 'Urgent', color: 'error' },
  'P1': { text: 'High', color: 'warning' },
  'P2': { text: 'Medium', color: 'info' },
  'P3': { text: 'Low', color: 'default' }
};

const ApplicationStatus = ({ applicationData = [] }) => {
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });
  const [roles, setRoles] = useState({});

  useEffect(() => {
    // Filter applications based on status and search term
    let filtered = [...applicationData];
    
    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(app => app.status === filter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(app => {
        const roleName = roles[app.roleId]?.title?.toLowerCase() || '';
        const company = roles[app.roleId]?.organization?.name?.toLowerCase() || '';
        return roleName.includes(lowerSearchTerm) || company.includes(lowerSearchTerm);
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'roleId') {
        aValue = roles[a.roleId]?.title || '';
        bValue = roles[b.roleId]?.title || '';
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    setFilteredApplications(filtered);
  }, [applicationData, filter, searchTerm, sortConfig, roles]);

  // Fetch role details for all applications
  useEffect(() => {
    const fetchRoles = async () => {
      const roleIds = [...new Set(applicationData.map(app => app.roleId))];
      
      try {
        const rolesData = {};
        for (const roleId of roleIds) {
          const response = await axios.get(`/api/roles/${roleId}`);
          rolesData[roleId] = response.data;
        }
        setRoles(rolesData);
      } catch (error) {
        console.error('Error fetching role details:', error);
      }
    };
    
    if (applicationData.length > 0) {
      fetchRoles();
    }
  }, [applicationData]);

  const handleFilterChange = (event, newFilter) => {
    setFilter(newFilter);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM DD, YYYY');
  };

  const getStatusProgress = (status) => {
    const statusMap = {
      'Applied': 20,
      'Screening': 40,
      'Interview': 60,
      'Offer': 100,
      'Rejected': 100,
      'On Hold': 50
    };
    return statusMap[status] || 0;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Job Applications
        </Typography>
        
        <TextField
          placeholder="Search applications..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Cancel fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ width: '300px' }}
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={filter} 
          onChange={handleFilterChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All Applications" value="all" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTime sx={{ mr: 0.5 }} fontSize="small" />
                Applied
              </Box>
            } 
            value="Applied" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Autorenew sx={{ mr: 0.5 }} fontSize="small" />
                Screening
              </Box>
            } 
            value="Screening" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <QuestionAnswer sx={{ mr: 0.5 }} fontSize="small" />
                Interview
              </Box>
            } 
            value="Interview" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ mr: 0.5 }} fontSize="small" />
                Offer
              </Box>
            } 
            value="Offer" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Cancel sx={{ mr: 0.5 }} fontSize="small" />
                Rejected
              </Box>
            } 
            value="Rejected" 
          />
        </Tabs>
      </Box>

      {applicationData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: 'rgba(0,0,0,0.02)' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            You haven't applied to any jobs yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Start exploring job opportunities and apply today!
          </Typography>
          <Button variant="contained" color="primary" href="/jobs">
            Browse Jobs
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
            <Table>
              <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                <TableRow>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('roleId')}
                  >
                    Job Title
                    {sortConfig.key === 'roleId' && (
                      <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {sortConfig.key === 'status' && (
                      <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('updatedAt')}
                  >
                    Last Updated
                    {sortConfig.key === 'updatedAt' && (
                      <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('priority')}
                  >
                    Priority
                    {sortConfig.key === 'priority' && (
                      <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApplications
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((application) => {
                    const role = roles[application.roleId] || {};
                    const organization = role.organization || {};
                    
                    return (
                      <TableRow key={application._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              sx={{ 
                                width: 40, 
                                height: 40, 
                                mr: 2, 
                                bgcolor: organization.color || 'primary.main' 
                              }}
                            >
                              {organization.name ? organization.name[0] : 'J'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {role.title || 'Unknown Position'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {organization.name || 'Unknown Company'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={application.status}
                            color={statusChipMap[application.status]?.color || 'default'}
                            size="small"
                            icon={statusChipMap[application.status]?.icon}
                            className="status-chip"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(application.updatedAt)}
                          </Typography>
                          {application.log && application.log.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {application.log[application.log.length - 1].message}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {application.priority && (
                            <Chip
                              label={priorityMap[application.priority]?.text || application.priority}
                              color={priorityMap[application.priority]?.color || 'default'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={getStatusProgress(application.status)} 
                                className="progress-bar"
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 5,
                                  backgroundColor: 'rgba(0,0,0,0.05)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 5,
                                    backgroundColor: application.status === 'Rejected' ? 'error.main' : 'primary.main'
                                  }
                                }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography variant="body2" color="text.secondary">
                                {`${getStatusProgress(application.status)}%`}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton 
                              color="primary"
                              size="small"
                              component="a"
                              href={`/applications/${application._id}`}
                            >
                              <RemoveRedEye fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                
                {filteredApplications.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        No applications found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredApplications.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
};

export default ApplicationStatus;
